<template>
  <div class="position-card-form">
    <PositionPageHeader
      class="position-header-form"
      :positionName="positionData?.name || name"
      :code="positionHeaderState.code"
      :status="positionHeaderState.status"
      :has_breadcrumb="true"
      :breadcrumbsList="computedBreadcrumbs"
      :chatterUnreadCount="totalPositionChatterData"
      :menuItems="dropdownItems"
      :accountId="accountId"
      :positionId="positionData?.id || computedPositionId"
      :apiBaseUrl="apiBaseUrl"
      :showExpandedDetails="ideaMDP443"
      @breadcrumb-click="handleBreadcrumbClick"
      @chatter-click="openPositionChatter"
      @menu-click="(item) => handleOptionClick({ item })"
    />
    <ChatterDrawer
      ref="drawerContainer"
      :drawer="chatterDrawerOpen"
      :iconName="'org_icon_position'"
      :type="'Position'"
      :typeValue="positionData?.name || name"
      object-type="position"
      :object-id="positionData?.id || computedPositionId || null"
      :chatterList="positionChatterData"
      :totalCheckpointChatter="totalPositionChatterData"
      @closeDrawer="onCloseChatterDrawer"
      @requestMoreCheckpointsChatter="getChatterData"
    />
    <PositionPageMenu
      :initial-tab="positionPageMenuInitialTab"
      :attached-roles-count="attachedRolesCount"
      :assignments-count="internalAssignmentsCount"
      :has_purpose="showContextTabV2 ? hasContext : hasPurpose"
      :show_context_count_badge="showContextCountBadge"
      :show-attached-responsibilities="ideaMdp585Enabled"
      :show-attached-roles-tab="showAttachedRolesTab"
      :context_files_count="contextFilesCount"
      :has_context="hasContext"
      @tab-selected="onPositionPageMenuTabSelected"
    />

    <div class="tabs-pane">
      <!-- ER-11574: whole-page skeleton while the position detail fetch is in flight. -->
      <v-skeleton-loader
        v-if="skeletonLoading"
        type="heading, button, article"
        class="position-skeleton-loader"
      />
      <template v-else>
      <div v-if="selectedTab === 0" class="tab-content tab-purpose">
        <!--
          ER-9892 / MDP-494: behind the idea-MDP-494 flag, render the new ContextTab;
          otherwise keep the legacy PurposeViewEdit.
        -->
        <ContextTab
          v-if="showContextTabV2"
          :state="contextState"
          :context="positionData?.context || null"
          :context-updated-at="positionData?.context_updated_at || null"
          :context-updated-by="positionData?.context_updated_by || null"
          :files="contextFiles"
          :uploading-files="uploadingContextFiles"
          :disabled="isSavingContext"
          :draft="contextDraft"
          :max-length="ENTITY_CONTEXT_MAX_LENGTH"
          :suggesting="isSuggestingContext"
          :suggest-error="contextSuggestError"
          @save="onContextSave"
          @suggest="onContextSuggest"
          @improve="onContextImprove"
          @input="setContextDraft"
          @discard="clearContextDraft"
          @upload="onContextUpload"
          @remove="onContextRemove"
        />
        <PurposeViewEdit
          v-else
          :value="positionData?.purpose || purpose"
          placeholder="Enter purpose for this position..."
          :maxLength="500"
          :isSaving="isSavingPurpose"
          @save="onPurposeSave"
          :hideAIButtons="true"
        />
      </div>
      <div
        v-if="showAttachedRolesTab && selectedTab === attachedRolesTabIndex"
        class="tab-content tab-attached-roles"
      >
        <PositionAttachedRolesTab
          ref="attachedRolesTabRef"
          :key="positionData?.id || positionId"
          :positionId="positionData?.id || positionId"
          :positionName="positionData?.name || name"
          :position-code="positionData?.code ?? code"
          :position-archived="Boolean(positionData?.archived ?? archived)"
          :show-tooltip-when-attached-roles-blocked="true"
          :drilldownInPanel="drilldownInPanel"
          :enable-attached-roles-reorder="enableAttachedRolesReorder"
          :allow-drag-role-to-position-tree="allowPaneTreeDragDrop"
          :on-role-drag-start-callback="onRoleDragStartCallback"
          :on-role-drag-end-callback="onRoleDragEndCallback"
          @drilldown="handleDrilldown"
          @refresh-position-data="handleRefreshPositionData"
          @attached-roles-search-sync="emitAttachedRolesSearchSync"
          @position-attached-roles-sync="emitPositionAttachedRolesSync"
        />
      </div>
      <div v-if="selectedTab === assignmentsTabIndex" class="tab-content tab-assignments">
        <PositionAssignmentsTable
          :key="`assignments-${positionData?.id || positionId}`"
          :dataStream="positionAssignmentsData"
          @onChatterClick="(data) => {}"
        />
      </div>
      </template>
    </div>

    <!-- Dialogs -->
    <RenamePositionDialog
      v-if="showRenamePositionDialog"
      v-model="showRenamePositionDialog"
      :name="positionData?.name || name"
      :error-message="renamePositionError"
      @clearError="renamePositionError = ''"
      @onSave="renamePosition"
    />

    <ClonePositionDialog
      v-if="showClonePositionDialog"
      v-model="showClonePositionDialog"
      :positionName="positionData?.name || name"
      @onAction="clonePosition"
    />

    <AttachedRole
      v-model="showAttachRoleDialog"
      OrgObjectType="Role to Position"
      @onSave="handleAttachRole"
      :items="attachableRoles"
      @item-selected="handleRoleSelected"
      :existingNamesList="attachedRoleNames"
      @add-item-selected="handleAddNewRole"
      :parent-name="positionData?.name || name"
      :openDefault="openDefault"
      :type="'attach_role_to_position_new_picker'"
      @load-more="loadMoreRoles"
      @search="searchRoles"
      :hasMoreData="hasMoreRolesData"
      :isLoadingMore="isLoadingMoreRoles"
      :isAssigning="isAssigningRole"
      :position="addAndAttachPosition"
      @created-and-attached="handleRoleCreatedAndAttached"
    />

    <RetirePositionDialog
      v-model="showRetirePositionDialog"
      :name="positionData?.name || name"
      @onAction="retirePositionAction"
    />

    <ReactivatePositionDialog
      v-model="showReactivatePositionDialog"
      :name="positionData?.name || name"
      @onAction="reactivatePositionAction"
    />

    <ArchivePositionDialog
      v-if="showArchiveDialog && archiveAction === 'archive'"
      v-model="showArchiveDialog"
      :name="positionData?.name || name"
      :error-message="archivePositionError"
      @onAction="handleArchivePosition"
    />

    <UnarchivePositionDialog
      v-if="showArchiveDialog && archiveAction === 'unarchive'"
      v-model="showArchiveDialog"
      :name="positionData?.name || name"
      :error-message="archivePositionError"
      @onAction="handleArchivePosition"
    />

    <ActivatePositionDialog
      v-model="showActivatePositionDialog"
      :name="positionData?.name || name"
      @onAction="activatePositionAction"
    />

    <DeletePositionDialog
      v-model="showDeletePositionDialog"
      :name="positionData?.name || name"
      @onAction="deletePositionAction"
    />

    <ContextIQOverlay
      v-if="contextIQOverlayEnabled"
      :model-value="isContextIQOverlayOpen"
      object-type="position"
      :object-id="positionData?.id"
      :object-name="positionData?.name || name"
      :context="contextOverlaySeed"
      @save="onContextOverlaySave"
      @close="isContextIQOverlayOpen = false"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, onMounted, watch, getCurrentInstance } from "vue";
import PositionPageHeader from "@components/PositionPageHeader/PositionPageHeader.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import { positionActionErrorMessage } from "@components/utils/positionErrorMessages";
import configService from "../../account/src/services/config";
import PositionPageMenu from "@components/PositionPageMenu/PositionPageMenu.vue";
import PurposeViewEdit from "@components/PurposeViewEdit/PurposeViewEdit.vue";
import ContextTab from "@components/ContextTab/ContextTab.vue";
import ContextIQOverlay from "@components/ContextIQOverlay/ContextIQOverlay.vue";
import {
  useContextSuggestion,
  ENTITY_CONTEXT_MAX_LENGTH,
} from "@components/utils/useContextSuggestion";
import launchDarklyService from "@/services/launchdarkly";
import { LD_FLAG_IDEA_MDP_494, LD_FLAG_IDEA_MDP_523, LD_FLAG_IDEA_MDP_560, LD_FLAG_IDEA_MDP_585 } from "@components/constants/launchDarklyFlagKeys";
import PositionAttachedRolesTab from "@components/PositionAttachedRolesTab/PositionAttachedRolesTab.vue";
import PositionAssignmentsTable from "@components/PositionAssignmentsTable/PositionAssignmentsTable.vue";
import RenamePositionDialog from "@components/PositionDialogs/RenamePositionDialog/RenamePositionDialog.vue";
import ClonePositionDialog from "@components/ClonePositionDialog/ClonePositionDialog.vue";
import AttachedRole from "@components/RolesCatalog/AttachedRole.vue";
import RetirePositionDialog from "@components/PositionDialogs/RetirePositionDialog/RetirePositionDialog.vue";
import ReactivatePositionDialog from "@components/PositionDialogs/ReactivatePositionDialog/ReactivatePositionDialog.vue";
import ArchivePositionDialog from "@components/PositionDialogs/ArchivePositionDialog/ArchivePositionDialog.vue";
import UnarchivePositionDialog from "@components/PositionDialogs/UnarchivePositionDialog/UnarchivePositionDialog.vue";
import ActivatePositionDialog from "@components/PositionDialogs/ActivatePositionDialog/ActivatePositionDialog.vue";
import DeletePositionDialog from "@components/PositionDialogs/DeletePositionDialog/DeletePositionDialog.vue";
import {
  getPositionId,
  getChatterList,
  updatePositionExcludeValue,
  updatePosition,
  clonePosition as clonePositionAPI,
  attachRoleToPosition,
  fetchAttachableRolesForPosition,
  createRole,
  retirePosition,
  reActivatePosition,
  archivePosition,
  unArchivePosition,
  activatePosition,
  deletePosition,
  getPositionAssignmentsByPositionId,
  getContextFiles,
  uploadContextFiles,
  deleteContextFile,
  mapContextFileToUi,
} from "@/api";
import {
  attachedRolesTabLabelForFlags,
  buildPositionPageMenuLabelToIndex,
  isPositionRolesAndResponsibilitiesUiEnabled,
  positionTabIndexToRouteValue,
  resolvePositionTabIndexFromRoute,
} from "@components/utils/positionFormTabs";

const props = defineProps({
  breadcrumbsList: {
    type: Array,
    default: () => [],
  },
  status: {
    type: String,
    default: "",
  },
  code: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  currentAssignmentsCount: {
    type: Number,
    default: 0,
  },
  purpose: {
    type: String,
    default: "",
  },
  positionId: {
    type: [Number, String],
    default: null,
  },
  archived: {
    type: Boolean,
    default: false,
  },
  /** When true (e.g. Positions Tree View), drilldown stays in same panel */
  drilldownInPanel: {
    type: Boolean,
    default: false,
  },
  /** Pane tree: drag role row to attach to another position (from OrganizationPositionsTab). */
  onRoleDragStartCallback: {
    type: Function,
    default: null,
  },
  onRoleDragEndCallback: {
    type: Function,
    default: null,
  },
  /**
   * LaunchDarkly (`dragDropAttachOrgObjectsPositionTreeMDP434`): when false in pane tree,
   * disables role reorder drag and role→tree attach. Full-page position keeps defaults.
   */
  allowPaneTreeDragDrop: {
    type: Boolean,
    default: true,
  },
});

const showAttachedRolesTab = computed(() => isPositionRolesAndResponsibilitiesUiEnabled());
const attachedRolesTabIndex = computed(() => (showAttachedRolesTab.value ? 1 : -1));
const assignmentsTabIndex = computed(() => (showAttachedRolesTab.value ? 2 : 1));

/** Pane: flag off disables reorder + tree attach; standalone position page keeps reorder. */
const enableAttachedRolesReorder = computed(
  () =>
    showAttachedRolesTab.value &&
    (!props.drilldownInPanel || props.allowPaneTreeDragDrop)
);

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const route = proxy?.$route;

/** LD `idea-MDP-443`: when on, render the new detailed PositionPageHeader
 *  (department / reporting-to / occupying / preparing badges). When off,
 *  fall back to the legacy simple header (breadcrumb + status + name + code). */
const ideaMDP443 = computed(() => Boolean(proxy?.$store?.getters?.["featureFlags/ideaMDP443"]));

const emit = defineEmits([
  "backToList",
  "drilldown",
  "refresh-position-data",
  "attached-roles-search-sync",
  "position-attached-roles-sync",
]);

const selectedTab = ref(isPositionRolesAndResponsibilitiesUiEnabled() ? 1 : 0);
/** True while the position detail fetch is in flight (drives the in-content skeleton). */
const isLoading = ref(false);
/** idea-MDP-560 rollout gate: show the whole-page skeleton instead of the legacy spinner. */
const skeletonLoadingEnabled = ref(false);
const skeletonLoading = computed(() => isLoading.value && skeletonLoadingEnabled.value);
const positionData = ref(null);
const accountId = ref(null);
const positionId = ref(null);
const positionChatterData = ref([]);
const totalPositionChatterData = ref(0);
const loadedPages = ref({});
const isSavingPurpose = ref(false);
/** idea-MDP-494: render the new ContextTab instead of the legacy purpose field. Starts false so the old UI shows until LD resolves. */
const showContextTabV2 = ref(false);
/** idea-MDP-523: Context tab numeric count badge (files + context text). Starts false until LD resolves. */
const showContextCountBadge = ref(false);
/** idea-MDP-585: new attached-roles tree + “Attached Roles and Responsibilities” tab label. */
const ideaMdp585Enabled = ref(false);
/** idea-MDP-523 (ER-10140): Suggest / Improve open the IQ Overlay instead of the inline draft. */
const contextIQOverlayEnabled = ref(false);
const isContextIQOverlayOpen = ref(false);
/** idea-MDP-494 ContextTab save-in-flight flag. */
const isSavingContext = ref(false);
/** ER-10218: IQ "Suggest" for the Context field — the suggestion is an unsaved draft. */
const {
  draft: contextDraft,
  isSuggesting: isSuggestingContext,
  suggestError: contextSuggestError,
  suggest: suggestContextText,
  setDraft: setContextDraft,
  clearDraft: clearContextDraft,
} = useContextSuggestion("position");
/** Uploaded context files for this position (ER-10294 context-files API). */
const contextFiles = ref([]);
/** Files mid-upload, rendered as progress-bar cards until the list refresh replaces them. */
const uploadingContextFiles = ref([]);
const chatterDrawerOpen = ref(false);

// Dialog states
const showRenamePositionDialog = ref(false);
const showClonePositionDialog = ref(false);
const showAttachRoleDialog = ref(false);
const showRetirePositionDialog = ref(false);
const showReactivatePositionDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref("archive"); // 'archive' or 'unarchive'
/** MDP-608: inline dialog errors for position rename / unarchive. */
const renamePositionError = ref("");
const archivePositionError = ref("");
const showActivatePositionDialog = ref(false);
const showDeletePositionDialog = ref(false);

// Attach role related states
const attachableRoles = ref([]);
const selectedRoleId = ref(null);
const openDefault = ref(false);
const hasMoreRolesData = ref(false);
const isLoadingMoreRoles = ref(false);
const isAssigningRole = ref(false);
const rolesPage = ref(1);
const rolesSearchTerm = ref("");
const rolesLimit = 10;
const attachedRolesCount = ref(0);
const attachedRolesTabRef = ref(null);
const positionAssignmentsData = ref([]);

const attachedRolesTabLabel = computed(() =>
  attachedRolesTabLabelForFlags(ideaMdp585Enabled.value)
);

const positionPageMenuLabelToIndex = computed(() =>
  buildPositionPageMenuLabelToIndex(showAttachedRolesTab.value, ideaMdp585Enabled.value)
);

/** Form tab index -> PositionPageMenu initialTab label (index 1 is flag-dependent). */
const positionPageMenuInitialTab = computed(() => {
  if (selectedTab.value === 0) return "Context";
  if (selectedTab.value === assignmentsTabIndex.value) return "History of Assignments";
  return attachedRolesTabLabel.value;
});

const internalAssignmentsCount = computed(() => positionAssignmentsData.value.length);

const hasPurpose = computed(() => {
  const p = positionData.value?.purpose ?? props.purpose;
  return Boolean(p && String(p).trim());
});

/** Non-empty context (V2) or purpose (legacy) — drives the Context tab count badge. */
const hasContext = computed(() => {
  if (showContextTabV2.value) {
    const context = positionData.value?.context;
    if (typeof context === "string") {
      return context.trim().length > 0;
    }
    return Boolean(context);
  }
  return hasPurpose.value;
});

/** The text the IQ Overlay opens on: what the editor currently shows (unsaved draft, else saved). */
const contextOverlaySeed = computed(
  () => contextDraft.value ?? positionData.value?.context ?? ""
);

/** Attached context files count for the Context tab badge. */
const contextFilesCount = computed(() => contextFiles.value?.length ?? 0);

/** ContextTab visual state: "filled" once a context has been saved, otherwise "empty". */
const contextState = computed(() =>
  (positionData.value?.context || "").trim() ? "filled" : "empty"
);

function onPositionPageMenuTabSelected(tabLabel) {
  const tabIndex = positionPageMenuLabelToIndex.value[tabLabel];
  if (tabIndex === undefined) return;
  onTabChange(tabIndex);
}

function onTabChange(tabIndex) {
  selectedTab.value = tabIndex;
  if (proxy.$router && route) {
    const positionTabValue = positionTabIndexToRouteValue(
      tabIndex,
      showAttachedRolesTab.value
    );

    proxy.$router
      .replace({
        ...route,
        query: {
          ...route.query,
          positionTab: positionTabValue,
        },
      })
      .catch(() => {});
  }
}

function handleBreadcrumbClick(breadcrumb) {
  if (breadcrumb.text === "Positions") {
    if (proxy?.$router) {
      proxy.$router.push("/organization/positions").catch(() => {});
    }
  }
}

function handleDrilldown(data) {
  emit("drilldown", data);
}

/** After clone, open the new position in the form (pane drilldown or detail route). */
async function navigateToClonedPosition(clonedPosition, cloneName) {
  if (!clonedPosition?.id) {
    emit("refresh-position-data");
    return;
  }

  const drilldownPayload = {
    positionId: clonedPosition.id,
    positionName: cloneName,
    name: cloneName,
    fromClone: true,
  };

  if (props.drilldownInPanel) {
    emit("drilldown", drilldownPayload);
    return;
  }

  const currentRoute = route;
  if (proxy?.$router && currentRoute?.params?.positionId) {
    await proxy.$router
      .push({
        name: currentRoute.name,
        params: { ...currentRoute.params, positionId: String(clonedPosition.id) },
        query: {
          ...currentRoute.query,
          positionName: cloneName,
        },
      })
      .catch(() => {});
    positionId.value = clonedPosition.id;
    await getPositionById(clonedPosition.id);
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;
    await refreshAttachedRolesTab();
    return;
  }

  emit("drilldown", drilldownPayload);
  emit("refresh-position-data");
}

async function handleRefreshPositionData() {
  const idPosition = positionData.value?.id || positionId.value;
  if (idPosition) {
    await getPositionById(idPosition);
    // Update the count from the refreshed position data
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;
    // Notify parent (e.g. tree view) to refresh the left panel
    emit("refresh-position-data");
  }
}

function emitAttachedRolesSearchSync(text) {
  emit("attached-roles-search-sync", text ?? "");
}

function emitPositionAttachedRolesSync(payload) {
  emit("position-attached-roles-sync", payload);
}

async function refreshAttachedRolesTab() {
  if (attachedRolesTabRef.value && typeof attachedRolesTabRef.value.refreshRoles === "function") {
    await attachedRolesTabRef.value.refreshRoles();
  }
}

/** Tab index from URL when opening / switching position. */
function tabIndexFromPositionRouteQuery() {
  const raw = route?.query?.positionTab;
  const { index, redirectTo } = resolvePositionTabIndexFromRoute(
    raw,
    showAttachedRolesTab.value
  );
  if (redirectTo && proxy?.$router && route) {
    proxy.$router
      .replace({
        ...route,
        query: { ...route.query, positionTab: redirectTo },
      })
      .catch(() => {});
  }
  return index;
}

const attachedRoleNames = computed(() => {
  return [];
});

const computedPositionId = computed(() => {
  return props.positionId || route?.params?.positionId || route?.query?.positionId || null;
});

const computedBreadcrumbs = computed(() => {
  if (props.breadcrumbsList && props.breadcrumbsList.length > 0) {
    return props.breadcrumbsList;
  }

  return [
    {
      text: "Positions",
      disabled: false,
      href: "/organization/positions",
    },
    {
      text: "Position",
      disabled: true,
    },
  ];
});

const positionHeaderState = computed(() => {
  return {
    status: positionData.value?.status || props.status,
    icon: "org_icon_position",
    label: "Position",
    code: positionData.value ? getOrgCodeName(positionData.value) : props.code,
    name: positionData.value?.name || props.name,
    archived: positionData.value?.archived ?? props.archived,
    assigned_employees: positionData.value?.assigned_employees || [],
  };
});

// Per-environment API base URL so PositionPageHeader's badge fetch works
// across dev/staging/prod without any hardcoded host.
const apiBaseUrl = computed(() => configService.get("baseURL") || "");

// Reuse the same dropdown logic the previous header used, so menu actions
// (rename / retire / clone / archive / etc.) keep working unchanged.
const dropdownItems = computed(() =>
  dropdownMixin.methods.getOrgObjectDropdownItems(positionHeaderState.value)
);

function openPositionChatter() {
  chatterDrawerOpen.value = true;
  reloadChatterData();
}

function onCloseChatterDrawer() {
  chatterDrawerOpen.value = false;
}

function getOrgCodeName(currentPosition) {
  const codeStr = currentPosition?.code_str;
  if (codeStr != null && codeStr !== "") {
    return String(codeStr).split(" ")[0];
  }
  return "#" + currentPosition?.code;
}

async function onPurposeSave(value) {
  try {
    isSavingPurpose.value = true;
    const idPosition = positionData.value?.id;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    const payload = {
      purpose: value,
    };
    await updatePositionExcludeValue(accountId.value, idPosition, payload);
    await getPositionById(idPosition);
  } catch (error) {
    console.error("Error updating purpose:", error);
  } finally {
    isSavingPurpose.value = false;
  }
}

async function onContextSave(value) {
  try {
    isSavingContext.value = true;
    const idPosition = positionData.value?.id;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    await updatePositionExcludeValue(accountId.value, idPosition, { context: value });
    await getPositionById(idPosition);
    clearContextDraft();
  } catch (error) {
    console.error("Error updating context:", error);
  } finally {
    isSavingContext.value = false;
  }
}

function onContextSuggest() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
    return undefined;
  }
  return suggestContextText(positionData.value?.id);
}

function onContextImprove() {
  if (contextIQOverlayEnabled.value) {
    isContextIQOverlayOpen.value = true;
  }
}

/** Keep the overlay's text as the editor draft until the save lands, so a failed save loses nothing. */
function onContextOverlaySave(value) {
  setContextDraft(value);
  return onContextSave(value);
}

async function onContextUpload(fileList) {
  const idPosition = positionData.value?.id;
  if (!fileList?.length || !idPosition) return;

  uploadingContextFiles.value = Array.from(fileList).map((file) => ({
    name: file.name,
    progress: 0,
  }));

  try {
    isSavingContext.value = true;
    await uploadContextFiles("position", idPosition, fileList, (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      uploadingContextFiles.value = uploadingContextFiles.value.map((file) => ({
        ...file,
        progress,
      }));
    });
    await loadContextFiles(idPosition);
  } catch (error) {
    console.error("Error uploading context file:", error);
  } finally {
    isSavingContext.value = false;
    uploadingContextFiles.value = [];
  }
}

async function onContextRemove(fileId) {
  const idPosition = positionData.value?.id;
  if (!fileId || !idPosition) return;

  try {
    isSavingContext.value = true;
    await deleteContextFile(fileId);
    await loadContextFiles(idPosition);
  } catch (error) {
    console.error("Error removing context file:", error);
  } finally {
    isSavingContext.value = false;
  }
}

async function getPositionById(idPosition) {
  try {
    const { data } = await getPositionId(accountId.value, idPosition);
    positionData.value = data;
    // Load context files immediately when flag is on — do not wait on assignments.
    if (showContextTabV2.value) {
      void loadContextFiles(idPosition);
    }
    await fetchPositionAssignments();
  } catch (error) {
    console.error("Error fetching position:", error);
  }
}

/** ER-10294: refresh the Context Files list for this position. */
async function loadContextFiles(idPosition) {
  if (!showContextTabV2.value) return;
  try {
    const { data } = await getContextFiles("position", idPosition);
    const list = Array.isArray(data) ? data : data?.results || [];
    contextFiles.value = list.map(mapContextFileToUi);
  } catch (error) {
    console.error("Error loading context files:", error);
  }
}

function mapAssignmentToTableRow(item) {
  const employee = item.employee && typeof item.employee === "object" ? item.employee : {};
  const name = String(
    item.employee_name ||
      item.employeeName ||
      [item.first_name ?? employee.first_name, item.last_name ?? employee.last_name]
        .filter(Boolean)
        .join(" ") ||
      ""
  ).trim();
  const parts = name.split(" ");
  const first_name = item.first_name ?? employee.first_name ?? parts[0] ?? "";
  const last_name = item.last_name ?? employee.last_name ?? parts.slice(1).join(" ") ?? "";
  return {
    assignment_id: item.id ?? item.assignment_id,
    employee_name: name || `${first_name} ${last_name}`.trim(),
    employee_avatar: item.employee_avatar ?? employee.avatar ?? item.avatar,
    position_name: item.position_name ?? item.title ?? positionData.value?.name ?? props.name,
    importance: item.importance ?? item.importance_level ?? 0,
    commitment: item.commitment ?? item.commitment_level ?? "Primary",
    showNewBadge: !!item.showNewBadge,
    fte:
      item.fte ??
      (item.allocation === 1 || item.allocation === 100
        ? 1
        : item.allocation === 0.5 || item.allocation === 50
          ? 0.5
          : null),
    assigned_by:
      typeof item.assigned_by === "object" && item.assigned_by !== null
        ? (item.assigned_by.name ?? "")
        : (item.assigned_by ?? ""),
    assigned_on: item.assigned_on ?? item.assigned_on_date ?? null,
    assigned_by_avatar:
      typeof item.assigned_by === "object" && item.assigned_by !== null
        ? (item.assigned_by.avatar ?? null)
        : (item.assigned_by_avatar ?? null),
    relieved_by: (() => {
      const removed = item.relieved_by ?? item.removed_by;
      return typeof removed === "object" && removed !== null
        ? (removed.name ?? null)
        : (removed ?? null);
    })(),
    relieved_on: item.relieved_on ?? item.removed_on ?? item.relieved_on_date ?? null,
    relieved_by_avatar: (() => {
      const removed = item.relieved_by ?? item.removed_by;
      return typeof removed === "object" && removed !== null
        ? (removed.avatar ?? null)
        : (item.relieved_by_avatar ?? null);
    })(),
    effective_from: item.effective_from ?? item.effectiveFrom ?? item.effective_on ?? item.effectiveOn ?? null,
    effective_to: item.effective_to ?? item.effectiveTo ?? item.effective_by ?? item.effectiveBy ?? null,
    effective_status: item.effective_status ?? item.effectiveStatus ?? null,
    state: item.state ?? null,
    chatterUnread: item.chatterUnread ?? item.chatter_unread ?? 0,
    first_name,
    last_name,
  };
}

async function fetchPositionAssignments() {
  const idPosition = positionData.value?.id || positionId.value;
  const account = accountId.value;
  if (!account || !idPosition) {
    positionAssignmentsData.value = [];
    return;
  }
  try {
    const { data } = await getPositionAssignmentsByPositionId(account, idPosition, { limit: 200 });
    const results = data?.results ?? data?.data ?? (Array.isArray(data) ? data : []);
    positionAssignmentsData.value = (results || []).map(mapAssignmentToTableRow);
  } catch (err) {
    // Fallback: use assigned_employees from position detail if API by position is not supported
    const assigned = positionData.value?.assigned_employees ?? [];
    positionAssignmentsData.value = assigned.map((emp) =>
      mapAssignmentToTableRow({
        id: emp.assignment_id ?? emp.id,
        first_name: emp.first_name ?? emp.firstName,
        last_name: emp.last_name ?? emp.lastName,
        employee_name: [emp.first_name ?? emp.firstName, emp.last_name ?? emp.lastName]
          .filter(Boolean)
          .join(" "),
        avatar: emp.avatar,
        title: positionData.value?.name,
        commitment: emp.commitment ?? "Primary",
        assigned_on: emp.assigned_on ?? emp.assigned_on_date,
        assigned_by: emp.assigned_by,
        relieved_on: emp.relieved_on ?? null,
        relieved_by: emp.relieved_by ?? null,
        effective_from: emp.effective_from ?? emp.effectiveFrom ?? emp.effective_on ?? emp.effectiveOn,
        effective_to: emp.effective_to ?? emp.effectiveTo ?? emp.effective_by ?? emp.effectiveBy,
        effective_status: emp.effective_status ?? emp.effectiveStatus,
        state: emp.state,
      })
    );
  }
}

async function fetchPositionData() {
  isLoading.value = true;
  try {
    if (!accountId.value || !positionId.value) {
      console.warn("Missing accountId or positionId:", {
        accountId: accountId.value,
        positionId: positionId.value,
      });
      return;
    }

    positionData.value = null;

    const { data } = await getPositionId(accountId.value, positionId.value);

    if (data) {
      positionData.value = data;
      // Load context files immediately when flag is on — do not wait on assignments.
      if (showContextTabV2.value) {
        void loadContextFiles(positionId.value);
      }
      await fetchPositionAssignments();
    } else {
      console.warn("No position data found for ID:", positionId.value);
      positionData.value = null;
    }
  } catch (error) {
    console.error("Error fetching position data:", error);
    console.error("Error details:", error.response?.data || error.message);
    positionData.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function reloadChatterData() {
  if (!positionData.value) return;

  const currentPositionId = positionData.value.id;

  positionChatterData.value = [];
  totalPositionChatterData.value = 0;
  if (loadedPages.value[currentPositionId]) {
    loadedPages.value[currentPositionId].clear();
  }

  await getChatterData();
}

async function getChatterData() {
  if (!positionData.value) return;

  const currentPositionId = positionData.value.id;
  const currentLength = positionChatterData.value.length;
  const page = Math.floor(currentLength / 10) + 1;

  if (!loadedPages.value[currentPositionId]) {
    loadedPages.value[currentPositionId] = new Set();
  }

  if (loadedPages.value[currentPositionId].has(page)) {
    return;
  }

  try {
    const { data } = await getChatterList(accountId.value, currentPositionId, "position", page);
    loadedPages.value[currentPositionId].add(page);

    if (data && data.count) {
      totalPositionChatterData.value = data.count;
    }

    if (data && data.results) {
      let chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const recordedByName = el.recorded_by_name != null ? String(el.recorded_by_name) : "";
        let nameParts = recordedByName.split(" ");
        const newEl = {
          id: el.id,
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };

        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: recordedByName,
            lastName: "",
          };
        }

        return newEl;
      });
      positionChatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching position chatter data:", error);
  }
}

function handleOptionClick(operation) {
  const action = operation.item?.value;

  switch (action) {
    case "activate_action":
      showActivatePositionDialog.value = true;
      break;
    case "retire_action":
      showRetirePositionDialog.value = true;
      break;
    case "reactivate_action":
      showReactivatePositionDialog.value = true;
      break;
    case "rename_action":
      renamePositionError.value = "";
      showRenamePositionDialog.value = true;
      break;
    case "delete_action":
      showDeletePositionDialog.value = true;
      break;
    case "archive_action":
      archiveAction.value = "archive";
      archivePositionError.value = "";
      showArchiveDialog.value = true;
      break;
    case "unarchive_action":
      archiveAction.value = "unarchive";
      archivePositionError.value = "";
      showArchiveDialog.value = true;
      break;
    case "attach_position_role":
      handleOpenAttachRole();
      break;
    case "cloned_action":
      showClonePositionDialog.value = true;
      break;
    default:
      break;
  }
}

async function handleOpenAttachRole() {
  showAttachRoleDialog.value = true;
  openDefault.value = true;
  rolesPage.value = 1;
  rolesSearchTerm.value = "";
  await fetchAttachableRolesList();
}

async function fetchAttachableRolesList(isLoadMore = false) {
  try {
    if (isLoadMore) {
      isLoadingMoreRoles.value = true;
    }

    const offset = (rolesPage.value - 1) * rolesLimit;
    const { data } = await fetchAttachableRolesForPosition(
      accountId.value,
      positionData.value?.id || positionId.value,
      rolesLimit,
      offset,
      rolesSearchTerm.value
    );

    if (isLoadMore) {
      const mappedResults = (data.results || []).map((item) => {
        const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || "";
        return {
          ...item,
          role_code: code,
          code: code,
          attached: item.attached_to_position || false,
          disabled: item.attached_to_position || false,
        };
      });
      attachableRoles.value.push(...mappedResults);
    } else {
      attachableRoles.value = (data.results || []).map((item) => {
        const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || "";
        return {
          ...item,
          role_code: code,
          code: code,
          attached: item.attached_to_position || false,
          disabled: item.attached_to_position || false,
        };
      });
    }

    hasMoreRolesData.value = !!data.next;
  } catch (error) {
    console.error("Error fetching attachable roles:", error);
    attachableRoles.value = [];
    hasMoreRolesData.value = false;
  } finally {
    if (isLoadMore) {
      isLoadingMoreRoles.value = false;
    }
  }
}

function handleRoleSelected(role) {
  if (!role || !role.id) return;
  selectedRoleId.value = role.id;
}

async function handleAddNewRole(roleData) {
  try {
    proxy.$loader?.setAppLoading(true);
    const newRole = await createRole(accountId.value, {
      name: roleData.name,
      account: accountId.value,
    });

    if (newRole && newRole.data && newRole.data.id) {
      selectedRoleId.value = newRole.data.id;
      await handleAttachRole();
    }
  } catch (error) {
    console.error("Error creating and attaching role:", error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleAttachRole() {
  if (!selectedRoleId.value) return;

  try {
    isAssigningRole.value = true;
    proxy.$loader?.setAppLoading(true);

    const payload = {
      attachment_id: selectedRoleId.value,
    };

    await attachRoleToPosition(
      accountId.value,
      positionData.value?.id || positionId.value,
      payload
    );

    showAttachRoleDialog.value = false;
    selectedRoleId.value = null;
    rolesPage.value = 1;
    rolesSearchTerm.value = "";

    await getPositionById(positionData.value?.id || positionId.value);

    // Update the attached roles count
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;

    // Refresh the attached roles tab if it exists
    await refreshAttachedRolesTab();

    // Notify parent (e.g. tree view) to refresh so the new role shows in the left panel
    emit("refresh-position-data");
  } catch (error) {
    console.error("Error attaching role:", error);
  } finally {
    isAssigningRole.value = false;
    proxy.$loader?.setAppLoading(false);
  }
}

// Position context for AddAndAttachRoleDialog ({ id, code, name })
const addAndAttachPosition = computed(() => {
  const code = String(positionData.value?.code ?? props.code ?? "")
    .trim()
    .replace(/^#/, "")
    .split(" ")[0];
  const id = positionData.value?.id || positionId.value;
  const positionName = positionData.value?.name || props.name;
  if (!id || !positionName || !code) return null;
  return { id, code, name: positionName };
});

// Role was created and attached in one call by AddAndAttachRoleDialog — just refresh.
async function handleRoleCreatedAndAttached() {
  showAttachRoleDialog.value = false;
  selectedRoleId.value = null;
  rolesPage.value = 1;
  rolesSearchTerm.value = "";

  await getPositionById(positionData.value?.id || positionId.value);
  attachedRolesCount.value = positionData.value?.roles_count ?? 0;
  await refreshAttachedRolesTab();
  emit("refresh-position-data");
}

async function loadMoreRoles() {
  rolesPage.value += 1;
  await fetchAttachableRolesList(true);
}

async function searchRoles(searchTerm) {
  rolesSearchTerm.value = searchTerm;
  rolesPage.value = 1;
  await fetchAttachableRolesList(false);
}

async function renamePosition(newName) {
  renamePositionError.value = "";
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    const payload = {
      name: newName,
      account: accountId.value,
    };
    await updatePosition(accountId.value, idPosition, payload);
    showRenamePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
    emit("refresh-position-data");
  } catch (error) {
    // MDP-608: keep the rename dialog open with the inline failure.
    renamePositionError.value = positionActionErrorMessage(error, "rename");
    console.error("Error renaming position:", error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function clonePosition() {
  try {
    proxy.$loader?.setAppLoading(true);
    const currentPositionName = (positionData.value?.name || props.name || "").trim();
    if (!currentPositionName) {
      throw new Error("No position name available to clone");
    }
    const originalPositionId = positionData.value?.id || positionId.value;
    if (!originalPositionId) {
      throw new Error("No position ID available to clone");
    }

    const cloneName = `Clone of ${currentPositionName}`;
    const { data: clonedPosition } = await clonePositionAPI(
      accountId.value,
      originalPositionId
    );

    showClonePositionDialog.value = false;

    proxy.$notifications?.success?.(
      "Position successfully cloned",
      `Position ${cloneName} was successfully created.`
    );

    await navigateToClonedPosition(clonedPosition, cloneName);

    return clonedPosition;
  } catch (error) {
    console.error("Error cloning position:", error);
    proxy.$notifications?.error?.(
      "Failed to clone position",
      error?.response?.data?.detail || "Please try again."
    );
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function retirePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    await retirePosition(accountId.value, idPosition);
    showRetirePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
    emit("refresh-position-data");
  } catch (error) {
    console.error("Error retiring position:", error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function reactivatePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    await reActivatePosition(accountId.value, idPosition);
    showReactivatePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
    emit("refresh-position-data");
  } catch (error) {
    console.error("Error reactivating position:", error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function handleArchivePosition() {
  const action = archiveAction.value;
  archivePositionError.value = "";
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error("No position ID");
    }

    if (action === "archive") {
      await archivePosition(accountId.value, "position", idPosition);
    } else {
      await unArchivePosition(accountId.value, "position", idPosition);
    }

    showArchiveDialog.value = false;
    if (action === "unarchive") {
      proxy.$notifications?.success?.("Position unarchived");
    }
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
    emit("refresh-position-data");
  } catch (error) {
    // MDP-608: keep the dialog open with the inline failure; finally clears loader.
    archivePositionError.value = positionActionErrorMessage(error, action);
    console.error(`Error ${action}ing position:`, error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function activatePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    await activatePosition(accountId.value, idPosition);
    showActivatePositionDialog.value = false;
    await getPositionById(idPosition);
    await refreshAttachedRolesTab();
    emit("refresh-position-data");
  } catch (error) {
    console.error("Error activating position:", error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

async function deletePositionAction() {
  try {
    proxy.$loader?.setAppLoading(true);
    const idPosition = positionData.value?.id || positionId.value;
    if (!idPosition) {
      throw new Error("No position ID");
    }
    const positionName = (positionData.value?.name || props.name || "").trim();
    await deletePosition(accountId.value, idPosition);
    showDeletePositionDialog.value = false;
    proxy.$notifications?.success?.(
      "Position successfully deleted",
      positionName
        ? `Position ${positionName} was successfully deleted`
        : "The position was successfully deleted."
    );

    const snapshot =
      typeof attachedRolesTabRef.value?.getAttachedRolesListSearch === "function"
        ? attachedRolesTabRef.value.getAttachedRolesListSearch()
        : "";
    emit("attached-roles-search-sync", snapshot ?? "");

    // Notify parent (e.g. tree view) to refresh so the deleted position is removed from the list
    emit("refresh-position-data");

    // Pane tree view: parent clears selection and route; avoid a full push that resets pane state.
    if (proxy?.$router && !props.drilldownInPanel) {
      proxy.$router.push("/organization/positions").catch(() => {});
    }
  } catch (error) {
    console.error("Error deleting position:", error);
  } finally {
    proxy.$loader?.setAppLoading(false);
  }
}

watch(
  computedPositionId,
  async (newPositionId, oldPositionId) => {
    if (newPositionId && newPositionId !== oldPositionId && accountId.value) {
      try {
        if (!skeletonLoadingEnabled.value) {
          proxy.$loader?.show();
        }
        positionId.value = newPositionId;

        positionData.value = null;
        positionChatterData.value = [];
        totalPositionChatterData.value = 0;
        selectedTab.value = tabIndexFromPositionRouteQuery();

        if (oldPositionId && loadedPages.value[oldPositionId]) {
          loadedPages.value[oldPositionId].clear();
        }

        await fetchPositionData();
      } catch (error) {
        console.error("Error fetching position data on change:", error);
      } finally {
        proxy.$loader?.hide();
      }
    }
  },
  { immediate: false }
);

watch(
  () => route?.query?.positionId,
  async (newPositionId, oldPositionId) => {
    if (newPositionId && newPositionId !== oldPositionId && accountId.value) {
      try {
        if (!skeletonLoadingEnabled.value) {
          proxy.$loader?.show();
        }
        positionId.value = newPositionId;

        positionData.value = null;
        positionChatterData.value = [];
        totalPositionChatterData.value = 0;
        selectedTab.value = tabIndexFromPositionRouteQuery();

        if (oldPositionId && loadedPages.value[oldPositionId]) {
          loadedPages.value[oldPositionId].clear();
        }

        await fetchPositionData();
      } catch (error) {
        console.error("Error fetching position data on route change:", error);
      } finally {
        proxy.$loader?.hide();
      }
    }
  },
  { immediate: false }
);

watch(selectedTab, (tab) => {
  if (tab === 2 && (positionData.value?.id || positionId.value) && accountId.value) {
    fetchPositionAssignments();
  }
});

onMounted(async () => {
  try {
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    positionId.value = computedPositionId.value;
    showContextTabV2.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_494, false)) === true;
    showContextCountBadge.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_523, false)) === true;
    contextIQOverlayEnabled.value = showContextCountBadge.value;
    ideaMdp585Enabled.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_585, false)) === true;
    skeletonLoadingEnabled.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_560, false)) === true;

    // ER-11574: keep the legacy full-screen spinner only when the skeleton rollout is off,
    // so the in-content skeleton is actually visible when idea-MDP-560 is on.
    if (!skeletonLoadingEnabled.value) {
      proxy.$loader?.show();
    }

    selectedTab.value = tabIndexFromPositionRouteQuery();

    await fetchPositionData();
    attachedRolesCount.value = positionData.value?.roles_count ?? 0;
  } finally {
    proxy.$loader?.hide();
  }
});
</script>

<style scoped>
.position-card-form {
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0px 1px 2px 0px #1018280f,
    0px 1px 3px 0px #1018281a;
  border: 1px solid #eaecf0;
  width: 100%;
}

.position-header-form {
  padding: 10px 0px 0px 10px;
}
.breadcrumbs-area-form {
  padding: 10px 0px 0px 5px !important;
}

::v-deep .position-header-form .header-header-container {
  position: relative;
  left: -10px;
}

::v-deep .position-header-form .header-org-menu {
  right: 0px !important;
  margin-right: 25px !important;
}

::v-deep .position-header-form .header-org-menu .header-chatter-type-icon {
  padding: 2px;
  left: 8px;
}

::v-deep .position-header-form .header-chatter-icon {
  margin-right: 7px !important;
}

.tab-purpose,
.tab-assignments {
  width: 100%;
  padding-block: 16px;
}

.position-skeleton-loader {
  padding: 16px;
}

::v-deep .roles-table-wrapper {
  border: none !important;
  box-shadow: none !important;
}
</style>
